import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiErr, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader, SectionCard, Button } from "@/components/PageUI";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import DobField from "@/components/DobField";
import AvatarUpload from "@/components/AvatarUpload";
import { toast } from "sonner";
import { ArrowLeft, Phone, HeartPulse, IdCard, Trophy, FileText, Upload, Trash2, Plus, Download, Bell, Clock, CheckCircle2, Sparkles, Copy, Pencil, CreditCard } from "lucide-react";
import { ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, Radar } from "recharts";

const ageFrom = (dob) => {
  if (!dob) return null;
  const d = new Date(dob);
  if (isNaN(d.getTime())) return null;
  const t = new Date();
  let a = t.getFullYear() - d.getFullYear();
  const m = t.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && t.getDate() < d.getDate())) a -= 1;
  return a;
};

export default function PlayerProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { sportMeta } = useAuth();
  const [player, setPlayer] = useState(null);
  const [ratings, setRatings] = useState({ averages: {}, overall: 0, count: 0 });
  const [attendance, setAttendance] = useState([]);
  const [stats, setStats] = useState({ games: 0, totals: {} });
  const [documents, setDocuments] = useState([]);
  const [docCat, setDocCat] = useState("waiver");
  const [newAward, setNewAward] = useState("");
  const [docRequests, setDocRequests] = useState([]);
  const [reqName, setReqName] = useState("");
  const [reqCat, setReqCat] = useState("waiver");
  const fileRef = useRef(null);
  const [teams, setTeams] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);

  const PLAYER_FIELDS = ["name", "team_id", "photo", "dob", "jersey_number", "position", "parent_name", "parent_email", "parent_phone", "medical_notes", "emergency_contact", "id_number", "height", "weight", "allergies", "address", "parent_photo", "consent_document", "awards", "documents"];
  const openEdit = () => {
    const seed = {};
    PLAYER_FIELDS.forEach((k) => { seed[k] = player[k] ?? (k === "awards" || k === "documents" ? [] : ""); });
    setEditForm(seed); setEditOpen(true);
  };
  const setEdit = (k, v) => setEditForm((f) => ({ ...f, [k]: v }));
  const onEditPhoto = (url) => { if (url) setEdit("photo", url); };
  const saveEdit = async () => {
    if (!editForm.name || !editForm.team_id) return toast.error("Name and team are required");
    setSavingEdit(true);
    try { await api.put(`/players/${id}`, editForm); toast.success("Athlete updated"); setEditOpen(false); loadPlayer(); }
    catch (e) { toast.error(apiErr(e)); } finally { setSavingEdit(false); }
  };
  const deletePlayer = async () => {
    if (!window.confirm(`Delete ${player?.name}? This permanently removes their profile, ratings and documents.`)) return;
    try { await api.delete(`/players/${id}`); toast.success("Athlete deleted"); navigate("/app/players"); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const [autopay, setAutopay] = useState(null);
  const loadPlayer = () => api.get(`/players/${id}`).then((r) => setPlayer(r.data)).catch(() => {});
  const loadDocs = () => api.get(`/players/${id}/documents`).then((r) => setDocuments(r.data)).catch(() => {});
  const loadReqs = () => api.get(`/document-requests`).then((r) => setDocRequests(r.data.filter((x) => x.player_id === id))).catch(() => {});

  useEffect(() => {
    loadPlayer();
    api.get("/teams").then((r) => setTeams(r.data)).catch(() => {});
    api.get("/fees/card-coverage").then((r) => setAutopay({ covered: r.data.covered.includes(id), last4: (r.data.cards || {})[id] || "" })).catch(() => {});
    api.get(`/ratings/summary?player_id=${id}`).then((r) => setRatings(r.data)).catch(() => {});
    api.get(`/attendance?player_id=${id}`).then((r) => setAttendance(r.data)).catch(() => {});
    api.get(`/players/${id}/stats`).then((r) => setStats(r.data)).catch(() => {});
    loadDocs();
    loadReqs();
    loadEvals();
  }, [id]);

  const createReq = async () => {
    if (!reqName.trim()) return;
    try { await api.post(`/document-requests`, { player_id: id, name: reqName, category: reqCat }); setReqName(""); toast.success("Document requested"); loadReqs(); }
    catch (err) { toast.error(apiErr(err)); }
  };
  const nudgeReq = async (reqId) => {
    try { await api.post(`/document-requests/${reqId}/nudge`); toast.success("Reminder emailed to parent"); }
    catch (err) { toast.error(apiErr(err, "Could not email the parent right now — please try again")); }
  };
  const delReq = async (reqId) => { await api.delete(`/document-requests/${reqId}`); loadReqs(); };

  const [aiEval, setAiEval] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [evals, setEvals] = useState([]);
  const [savingEval, setSavingEval] = useState(false);
  const loadEvals = () => api.get(`/players/${id}/evaluations`).then((r) => setEvals(r.data)).catch(() => {});
  const saveEval = async () => {
    if (!aiEval.trim()) return;
    setSavingEval(true);
    try {
      const { data } = await api.post(`/players/${id}/evaluations`, { text: aiEval, share: true });
      const via = data.shared_via || [];
      toast.success(via.length ? `Saved & shared via ${via.join(" + ")}` : "Saved to profile");
      setAiEval(""); loadEvals();
    } catch (e) { toast.error(apiErr(e)); } finally { setSavingEval(false); }
  };
  const generateEval = async () => {
    setAiLoading(true); setAiEval("");
    try {
      const res = await fetch(`${API}/ai/player-evaluation`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(localStorage.getItem("cs_token") ? { Authorization: `Bearer ${localStorage.getItem("cs_token")}` } : {}) },
        credentials: "include",
        body: JSON.stringify({ player_id: id }),
      });
      if (!res.ok || !res.body) { toast.error("Could not generate evaluation"); return; }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n"); buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const d = JSON.parse(line.slice(6));
          if (d.delta) setAiEval((o) => o + d.delta);
        }
      }
    } catch { toast.error("Could not generate evaluation"); }
    finally { setAiLoading(false); }
  };

  const onUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 6_000_000) return toast.error("File too large (max 6MB)");
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await api.post(`/players/${id}/documents`, { name: file.name, category: docCat, content_type: file.type, data: reader.result });
        toast.success("Document uploaded"); loadDocs();
      } catch (err) { toast.error(apiErr(err)); }
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const delDoc = async (docId) => { await api.delete(`/documents/${docId}`); loadDocs(); };
  const dlDoc = (d) => { const a = document.createElement("a"); a.href = d.data; a.download = d.name; a.click(); };
  const addAward = async () => { if (!newAward.trim()) return; await api.post(`/players/${id}/awards`, { award: newAward }); setNewAward(""); loadPlayer(); };
  const delAward = async (i) => { await api.delete(`/players/${id}/awards/${i}`); loadPlayer(); };

  if (!player) return <Loader />;

  const attPct = attendance.length
    ? Math.round(attendance.filter((a) => ["present", "late"].includes(a.status)).length / attendance.length * 100) : 0;
  const radarData = Object.entries(ratings.averages).map(([k, v]) => ({ category: k, value: v }));

  return (
    <div className="animate-fade-up">
      <Button variant="ghost" onClick={() => navigate("/app/players")} className="mb-4 -ml-2" data-testid="back-players-btn"><ArrowLeft size={16} className="mr-1.5" /> Athletes</Button>

      <div className="rounded-2xl border border-border bg-card p-6 flex flex-col sm:flex-row items-start gap-6">
        {player.photo ? <img src={player.photo} alt="" className="h-24 w-24 rounded-2xl object-cover border border-border" />
          : <div className="h-24 w-24 rounded-2xl brand-gradient flex items-center justify-center text-white text-3xl font-bold">{player.name[0]}</div>}
        <div className="flex-1">
          <h1 className="font-heading font-extrabold text-2xl tracking-tight" data-testid="player-profile-name">{player.name}</h1>
          <div className="flex flex-wrap gap-2 mt-3">
            {player.jersey_number && <span className="text-sm bg-accent px-3 py-1 rounded-full">#{player.jersey_number}</span>}
            {player.position && <span className="text-sm bg-accent px-3 py-1 rounded-full">{player.position}</span>}
            {player.dob && <span className="text-sm bg-accent px-3 py-1 rounded-full" data-testid="player-dob-age">DOB {player.dob}{ageFrom(player.dob) !== null ? ` · Age ${ageFrom(player.dob)}` : ""}</span>}
          </div>
          <div className="flex gap-2 mt-4">
            <Button size="sm" variant="outline" onClick={openEdit} data-testid="edit-player-btn"><Pencil size={14} className="mr-1.5" /> Edit</Button>
            <Button size="sm" variant="outline" onClick={deletePlayer} className="text-destructive hover:text-destructive hover:bg-destructive/10" data-testid="delete-player-btn"><Trash2 size={14} className="mr-1.5" /> Delete</Button>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-4 text-center">
          <div><p className="font-heading font-extrabold text-2xl">{ratings.overall || "—"}</p><p className="text-xs text-muted-foreground">Rating</p></div>
          <div><p className="font-heading font-extrabold text-2xl text-secondary">{attPct}%</p><p className="text-xs text-muted-foreground">Attendance</p></div>
          <div><p className="font-heading font-extrabold text-2xl">{stats.games}</p><p className="text-xs text-muted-foreground">Games</p></div>
        </div>
      </div>

      <Tabs defaultValue="overview" className="mt-5">
        <TabsList data-testid="profile-tabs">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="performance">Performance</TabsTrigger>
          <TabsTrigger value="stats">Statistics</TabsTrigger>
          <TabsTrigger value="documents">Documents</TabsTrigger>
          <TabsTrigger value="info">Info</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4">
          <div className="grid lg:grid-cols-2 gap-4">
            <SectionCard title="Performance Profile" testid="profile-radar">
              {radarData.length ? (
                <ResponsiveContainer width="100%" height={280}>
                  <RadarChart data={radarData}>
                    <PolarGrid stroke="#2a3038" />
                    <PolarAngleAxis dataKey="category" tick={{ fill: "#94a3b8", fontSize: 11 }} />
                    <Radar dataKey="value" stroke="#007AFF" fill="#007AFF" fillOpacity={0.4} />
                  </RadarChart>
                </ResponsiveContainer>
              ) : <p className="text-sm text-muted-foreground py-10 text-center">No ratings yet. Add ratings in Performance.</p>}
            </SectionCard>
            <SectionCard title="Awards" testid="profile-awards">
              <div className="flex gap-2 mb-3">
                <Input value={newAward} onChange={(e) => setNewAward(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addAward()} placeholder="Add an award" data-testid="award-input" />
                <Button onClick={addAward} size="icon" className="brand-gradient text-white border-0 shrink-0" data-testid="add-award-btn"><Plus size={16} /></Button>
              </div>
              {player.awards?.length ? player.awards.map((a, i) => (
                <div key={i} className="flex items-center justify-between gap-2 py-2 border-b border-border last:border-0">
                  <span className="flex items-center gap-2"><Trophy size={16} className="text-secondary" /> {a}</span>
                  <button onClick={() => delAward(i)} className="text-muted-foreground hover:text-destructive" data-testid={`del-award-${i}`}><Trash2 size={14} /></button>
                </div>
              )) : <p className="text-sm text-muted-foreground py-6 text-center">No awards yet.</p>}
            </SectionCard>
          </div>
        </TabsContent>

        <TabsContent value="performance" className="mt-4">
          <SectionCard title={`Category Averages (${ratings.count} evaluations)`}>
            <div className="grid sm:grid-cols-2 gap-4">
              {Object.entries(ratings.averages).map(([k, v]) => (
                <div key={k}>
                  <div className="flex justify-between text-sm mb-1"><span>{k}</span><span className="font-semibold">{v}/10</span></div>
                  <div className="h-2 rounded-full bg-accent overflow-hidden"><div className="h-full brand-gradient" style={{ width: `${v * 10}%` }} /></div>
                </div>
              ))}
              {!Object.keys(ratings.averages).length && <p className="text-sm text-muted-foreground">No ratings yet.</p>}
            </div>
          </SectionCard>

          <SectionCard title="AI Evaluation" className="mt-4" testid="ai-eval-card"
            action={
              <Button size="sm" onClick={generateEval} disabled={aiLoading} className="brand-gradient text-white border-0" data-testid="ai-eval-btn">
                <Sparkles size={14} className="mr-1.5" /> {aiLoading ? "Generating..." : "Generate parent-ready note"}
              </Button>
            }>
            {aiEval ? (
              <div className="space-y-3">
                <p className="text-sm leading-relaxed whitespace-pre-wrap" data-testid="ai-eval-output">{aiEval}</p>
                {!aiLoading && (
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" onClick={saveEval} disabled={savingEval} className="brand-gradient text-white border-0" data-testid="ai-eval-save-btn"><CheckCircle2 size={14} className="mr-1.5" /> {savingEval ? "Sharing..." : "Save & share with parent"}</Button>
                    <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(aiEval); toast.success("Copied"); }} data-testid="ai-eval-copy"><Copy size={14} className="mr-1.5" /> Copy</Button>
                  </div>
                )}
              </div>
            ) : <p className="text-sm text-muted-foreground py-6 text-center">Generate a warm, parent-ready summary from this athlete's ratings, attendance and stats.</p>}
            {evals.length > 0 && (
              <div className="mt-5 pt-4 border-t border-border space-y-3" data-testid="saved-evals">
                <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Saved evaluations</p>
                {evals.map((ev) => (
                  <div key={ev.id} className="rounded-xl border border-border p-3" data-testid={`saved-eval-${ev.id}`}>
                    <p className="text-sm whitespace-pre-wrap">{ev.text}</p>
                    <p className="text-xs text-muted-foreground mt-2">{ev.author} · {ev.created_at?.slice(0, 10)}{ev.shared ? " · shared with parent" : ""}</p>
                  </div>
                ))}
              </div>
            )}
          </SectionCard>
        </TabsContent>

        <TabsContent value="stats" className="mt-4">
          <SectionCard title={`${sportMeta?.label || "Match"} Statistics`}>
            {Object.keys(stats.totals).length ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {Object.entries(stats.totals).map(([k, v]) => (
                  <div key={k} className="rounded-xl border border-border p-4"><p className="font-heading font-extrabold text-2xl">{v}</p><p className="text-xs text-muted-foreground">{k}</p></div>
                ))}
              </div>
            ) : <p className="text-sm text-muted-foreground">No match stats yet. Record stats in Matches.</p>}
          </SectionCard>
        </TabsContent>

        <TabsContent value="documents" className="mt-4">
          <SectionCard title="Request from parent" testid="profile-doc-requests"
            action={
              <div className="flex items-center gap-2">
                <Input value={reqName} onChange={(e) => setReqName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && createReq()} placeholder="e.g. Signed waiver" className="h-9 w-44" data-testid="doc-req-name-input" />
                <Select value={reqCat} onValueChange={setReqCat}>
                  <SelectTrigger className="h-9 w-28" data-testid="doc-req-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["waiver", "medical", "award", "id", "other"].map((c) => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button size="sm" onClick={createReq} className="brand-gradient text-white border-0" data-testid="create-doc-req-btn"><Plus size={14} className="mr-1.5" /> Request</Button>
              </div>
            }>
            {docRequests.length === 0 ? (
              <p className="text-sm text-muted-foreground py-6 text-center">No requests. Ask the parent to upload a waiver, medical form or ID.</p>
            ) : (
              <div className="divide-y divide-border">
                {docRequests.map((r) => (
                  <div key={r.id} className="flex items-center justify-between gap-3 py-3" data-testid={`doc-req-${r.id}`}>
                    <div className="flex items-center gap-3 min-w-0">
                      {r.status === "fulfilled"
                        ? <CheckCircle2 size={18} className="text-secondary shrink-0" />
                        : <Clock size={18} className="text-amber-400 shrink-0" />}
                      <div className="min-w-0">
                        <p className="font-medium text-sm truncate">{r.name}</p>
                        <p className="text-xs text-muted-foreground capitalize">{r.category} · {r.status === "fulfilled" ? "uploaded by parent" : "pending"}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {r.status !== "fulfilled" && (
                        <button onClick={() => nudgeReq(r.id)} className="text-muted-foreground hover:text-primary" title="Email reminder" data-testid={`nudge-doc-req-${r.id}`}><Bell size={16} /></button>
                      )}
                      <button onClick={() => delReq(r.id)} className="text-muted-foreground hover:text-destructive" data-testid={`del-doc-req-${r.id}`}><Trash2 size={16} /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </SectionCard>

          <SectionCard title="Documents" testid="profile-documents" className="mt-4"
            action={
              <div className="flex items-center gap-2">
                <Select value={docCat} onValueChange={setDocCat}>
                  <SelectTrigger className="h-9 w-32" data-testid="doc-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["waiver", "medical", "award", "id", "other"].map((c) => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}
                  </SelectContent>
                </Select>
                <input ref={fileRef} type="file" className="hidden" onChange={onUpload} data-testid="doc-file-input" />
                <Button size="sm" onClick={() => fileRef.current?.click()} className="brand-gradient text-white border-0" data-testid="upload-doc-btn"><Upload size={14} className="mr-1.5" /> Upload</Button>
              </div>
            }>
            {documents.length === 0 ? (
              <p className="text-sm text-muted-foreground py-8 text-center">No documents. Upload waivers, medical forms or ID.</p>
            ) : (
              <div className="divide-y divide-border">
                {documents.map((d) => (
                  <div key={d.id} className="flex items-center justify-between gap-3 py-3" data-testid={`doc-${d.id}`}>
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="h-9 w-9 rounded-lg bg-accent flex items-center justify-center shrink-0"><FileText size={16} className="text-primary" /></div>
                      <div className="min-w-0">
                        <p className="font-medium text-sm truncate">{d.name}</p>
                        <p className="text-xs text-muted-foreground capitalize">{d.category} · by {d.uploaded_by}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button onClick={() => dlDoc(d)} className="text-muted-foreground hover:text-primary" data-testid={`dl-doc-${d.id}`}><Download size={16} /></button>
                      <button onClick={() => delDoc(d.id)} className="text-muted-foreground hover:text-destructive" data-testid={`del-doc-${d.id}`}><Trash2 size={16} /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </SectionCard>
        </TabsContent>

        <TabsContent value="info" className="mt-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <SectionCard title="Parent & Emergency">
              <div className="space-y-3 text-sm">
                <p className="flex items-center gap-2"><IdCard size={15} className="text-muted-foreground" /> {player.parent_name || "—"}</p>
                <p className="flex items-center gap-2"><Phone size={15} className="text-muted-foreground" /> {player.parent_phone || "—"} · {player.parent_email || "—"}</p>
                <p className="flex items-center gap-2"><Phone size={15} className="text-destructive" /> Emergency: {player.emergency_contact || "—"}</p>
                <p className="flex items-center gap-2"><IdCard size={15} className="text-muted-foreground" /> ID: {player.id_number || "—"}</p>
                {autopay?.covered ? (
                  <p className="flex items-center gap-2 text-secondary font-medium" data-testid="autopay-badge"><CreditCard size={15} /> Autopay on{autopay.last4 ? ` •••• ${autopay.last4}` : ""}</p>
                ) : (
                  <p className="flex items-center gap-2 text-muted-foreground/70" data-testid="autopay-badge-none"><CreditCard size={15} /> No card on file</p>
                )}
              </div>
            </SectionCard>
            <SectionCard title="Medical Notes">
              <p className="text-sm flex items-start gap-2"><HeartPulse size={15} className="text-destructive mt-0.5" /> {player.medical_notes || "No medical notes."}</p>
            </SectionCard>
          </div>
        </TabsContent>
      </Tabs>

      {editForm && (
        <Dialog open={editOpen} onOpenChange={setEditOpen}>
          <DialogContent className="w-[calc(100%-1.5rem)] max-w-lg max-h-[88vh] overflow-y-auto rounded-2xl" data-testid="edit-player-dialog">
            <DialogHeader><DialogTitle>Edit athlete</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <AvatarUpload value={editForm.photo} onFile={onEditPhoto} testid="edit-player-photo" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><Label>Name *</Label><Input value={editForm.name} onChange={(e) => setEdit("name", e.target.value)} className="mt-1.5 h-11" data-testid="edit-player-name" /></div>
                <div><Label>Team *</Label>
                  <Select value={editForm.team_id} onValueChange={(v) => setEdit("team_id", v)}>
                    <SelectTrigger className="mt-1.5 h-11" data-testid="edit-player-team"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{teams.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="sm:col-span-2"><Label>Date of birth</Label><DobField value={editForm.dob} onChange={(v) => setEdit("dob", v)} testid="edit-player-dob" /></div>
                <div><Label>Jersey #</Label><Input value={editForm.jersey_number} onChange={(e) => setEdit("jersey_number", e.target.value)} className="mt-1.5 h-11" /></div>
                <div><Label>Position</Label>
                  {(sportMeta?.positions || []).length ? (
                    <Select value={editForm.position} onValueChange={(v) => setEdit("position", v)}>
                      <SelectTrigger className="mt-1.5 h-11" data-testid="edit-player-position"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>{sportMeta.positions.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                    </Select>
                  ) : <Input value={editForm.position} onChange={(e) => setEdit("position", e.target.value)} className="mt-1.5 h-11" />}
                </div>
                <div><Label>ID number</Label><Input value={editForm.id_number} onChange={(e) => setEdit("id_number", e.target.value)} className="mt-1.5 h-11" /></div>
                <div><Label>Parent name</Label><Input value={editForm.parent_name} onChange={(e) => setEdit("parent_name", e.target.value)} className="mt-1.5 h-11" /></div>
                <div><Label>Parent email</Label><Input value={editForm.parent_email} onChange={(e) => setEdit("parent_email", e.target.value)} className="mt-1.5 h-11" /></div>
                <div><Label>Parent phone</Label><Input value={editForm.parent_phone} onChange={(e) => setEdit("parent_phone", e.target.value)} className="mt-1.5 h-11" /></div>
                <div><Label>Emergency contact</Label><Input value={editForm.emergency_contact} onChange={(e) => setEdit("emergency_contact", e.target.value)} className="mt-1.5 h-11" /></div>
                <div><Label>Height</Label><Input value={editForm.height} onChange={(e) => setEdit("height", e.target.value)} className="mt-1.5 h-11" /></div>
                <div><Label>Weight</Label><Input value={editForm.weight} onChange={(e) => setEdit("weight", e.target.value)} className="mt-1.5 h-11" /></div>
                <div className="sm:col-span-2"><Label>Allergies</Label><Input value={editForm.allergies} onChange={(e) => setEdit("allergies", e.target.value)} className="mt-1.5 h-11" /></div>
                <div className="sm:col-span-2"><Label>Address</Label><Input value={editForm.address} onChange={(e) => setEdit("address", e.target.value)} className="mt-1.5 h-11" /></div>
                <div className="sm:col-span-2"><Label>Medical notes</Label><Textarea value={editForm.medical_notes} onChange={(e) => setEdit("medical_notes", e.target.value)} className="mt-1.5" rows={3} /></div>
              </div>
            </div>
            <DialogFooter><Button onClick={saveEdit} disabled={savingEdit} className="w-full sm:w-auto h-11 brand-gradient text-white border-0" data-testid="save-edit-player-btn">{savingEdit ? "Saving..." : "Save changes"}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
